import React from 'react';

interface DomainExtension {
  tld: string;
  price: number;
  category: string;
  popular?: boolean;
}

interface DomainExtensionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DomainExtensionsModal: React.FC<DomainExtensionsModalProps> = ({ isOpen, onClose }) => {
  const domainExtensions: DomainExtension[] = [
    // Generic Top Level Domains (gTLD) - Domain Pricing (Updated July 2025)
    { tld: '.com', price: 15.00, category: 'Popular', popular: true },
    { tld: '.net', price: 18.00, category: 'Popular', popular: true },
    { tld: '.org', price: 16.00, category: 'Popular', popular: true },
    { tld: '.info', price: 13.00, category: 'Popular' },
    { tld: '.biz', price: 13.00, category: 'Business' },
    
    // New Generic Top Level Domains (new gTLD) - Domain Pricing
    { tld: '.app', price: 18.00, category: 'Technology' },
    { tld: '.dev', price: 17.00, category: 'Technology' },
    { tld: '.io', price: 64.00, category: 'Technology', popular: true },
    { tld: '.tech', price: 48.00, category: 'Technology' },
    { tld: '.cloud', price: 23.00, category: 'Technology' },
    { tld: '.digital', price: 33.00, category: 'Technology' },
    { tld: '.online', price: 38.00, category: 'Technology' },
    { tld: '.website', price: 23.00, category: 'Technology' },
    { tld: '.site', price: 30.00, category: 'Technology' },
    
    // Business & Professional
    { tld: '.business', price: 8.99, category: 'Business' },
    { tld: '.company', price: 8.99, category: 'Business' },
    { tld: '.corp', price: 199.99, category: 'Business' },
    { tld: '.inc', price: 199.99, category: 'Business' },
    { tld: '.llc', price: 59.99, category: 'Business' },
    { tld: '.ltd', price: 24.99, category: 'Business' },
    { tld: '.enterprises', price: 34.99, category: 'Business' },
    { tld: '.solutions', price: 24.99, category: 'Business' },
    { tld: '.services', price: 34.99, category: 'Business' },
    { tld: '.consulting', price: 34.99, category: 'Business' },
    { tld: '.management', price: 24.99, category: 'Business' },
    { tld: '.finance', price: 54.99, category: 'Business' },
    { tld: '.financial', price: 54.99, category: 'Business' },
    { tld: '.money', price: 34.99, category: 'Business' },
    { tld: '.marketing', price: 34.99, category: 'Business' },
    { tld: '.agency', price: 24.99, category: 'Business' },
    
    // E-commerce & Retail
    { tld: '.store', price: 34.99, category: 'E-commerce' },
    { tld: '.shop', price: 39.99, category: 'E-commerce' },
    { tld: '.sale', price: 34.99, category: 'E-commerce' },
    { tld: '.discount', price: 34.99, category: 'E-commerce' },
    { tld: '.deals', price: 34.99, category: 'E-commerce' },
    { tld: '.shopping', price: 34.99, category: 'E-commerce' },
    
    // Creative & Design
    { tld: '.design', price: 54.99, category: 'Creative' },
    { tld: '.art', price: 19.99, category: 'Creative' },
    { tld: '.gallery', price: 24.99, category: 'Creative' },
    { tld: '.studio', price: 29.99, category: 'Creative' },
    { tld: '.media', price: 34.99, category: 'Creative' },
    { tld: '.photography', price: 24.99, category: 'Creative' },
    { tld: '.graphics', price: 24.99, category: 'Creative' },
    
    // Personal & Lifestyle
    { tld: '.me', price: 19.99, category: 'Personal' },
    { tld: '.life', price: 34.99, category: 'Personal' },
    { tld: '.style', price: 34.99, category: 'Personal' },
    { tld: '.blog', price: 34.99, category: 'Personal' },
    { tld: '.news', price: 29.99, category: 'Personal' },
    { tld: '.family', price: 29.99, category: 'Personal' },
    { tld: '.love', price: 34.99, category: 'Personal' },
    { tld: '.wedding', price: 34.99, category: 'Personal' },
    
    // Education & Non-Profit
    { tld: '.education', price: 24.99, category: 'Education' },
    { tld: '.school', price: 34.99, category: 'Education' },
    { tld: '.university', price: 54.99, category: 'Education' },
    { tld: '.academy', price: 34.99, category: 'Education' },
    { tld: '.training', price: 34.99, category: 'Education' },
    { tld: '.institute', price: 24.99, category: 'Education' },
    { tld: '.foundation', price: 34.99, category: 'Non-Profit' },
    { tld: '.charity', price: 34.99, category: 'Non-Profit' },
    
    // Health & Medical
    { tld: '.health', price: 84.99, category: 'Health' },
    { tld: '.medical', price: 54.99, category: 'Health' },
    { tld: '.healthcare', price: 54.99, category: 'Health' },
    { tld: '.doctor', price: 119.99, category: 'Health' },
    { tld: '.dental', price: 54.99, category: 'Health' },
    { tld: '.clinic', price: 54.99, category: 'Health' },
    { tld: '.surgery', price: 54.99, category: 'Health' },
    
    // Real Estate
    { tld: '.realestate', price: 119.99, category: 'Real Estate' },
    { tld: '.property', price: 159.99, category: 'Real Estate' },
    { tld: '.properties', price: 24.99, category: 'Real Estate' },
    { tld: '.homes', price: 24.99, category: 'Real Estate' },
    { tld: '.house', price: 34.99, category: 'Real Estate' },
    { tld: '.land', price: 34.99, category: 'Real Estate' },
    
    // Food & Hospitality
    { tld: '.restaurant', price: 54.99, category: 'Food' },
    { tld: '.food', price: 84.99, category: 'Food' },
    { tld: '.cafe', price: 34.99, category: 'Food' },
    { tld: '.kitchen', price: 54.99, category: 'Food' },
    { tld: '.menu', price: 39.99, category: 'Food' },
    { tld: '.recipes', price: 54.99, category: 'Food' },
    { tld: '.hotel', price: 84.99, category: 'Hospitality' },
    { tld: '.travel', price: 119.99, category: 'Hospitality' },
    { tld: '.vacation', price: 34.99, category: 'Hospitality' },
    
    // Sports & Recreation
    { tld: '.sport', price: 39.99, category: 'Sports' },
    { tld: '.sports', price: 39.99, category: 'Sports' },
    { tld: '.fitness', price: 34.99, category: 'Sports' },
    { tld: '.golf', price: 54.99, category: 'Sports' },
    { tld: '.soccer', price: 24.99, category: 'Sports' },
    { tld: '.football', price: 24.99, category: 'Sports' },
    { tld: '.basketball', price: 59.99, category: 'Sports' },
    { tld: '.tennis', price: 54.99, category: 'Sports' },
    
    // Geographic Extensions (Examples)
    { tld: '.us', price: 9.99, category: 'Country' },
    { tld: '.uk', price: 9.99, category: 'Country' },
    { tld: '.ca', price: 14.99, category: 'Country' },
    { tld: '.au', price: 14.99, category: 'Country' },
    { tld: '.de', price: 9.99, category: 'Country' },
    { tld: '.fr', price: 9.99, category: 'Country' },
    { tld: '.jp', price: 39.99, category: 'Country' },
    { tld: '.in', price: 9.99, category: 'Country' },
    { tld: '.cn', price: 9.99, category: 'Country' },
    { tld: '.br', price: 14.99, category: 'Country' },
    { tld: '.mx', price: 34.99, category: 'Country' },
    { tld: '.es', price: 9.99, category: 'Country' },
    { tld: '.it', price: 9.99, category: 'Country' },
    { tld: '.nl', price: 9.99, category: 'Country' },
    { tld: '.ru', price: 4.99, category: 'Country' },
    
    // More niche extensions
    { tld: '.xyz', price: 1.99, category: 'Generic' },
    { tld: '.top', price: 2.99, category: 'Generic' },
    { tld: '.win', price: 2.99, category: 'Generic' },
    { tld: '.click', price: 2.99, category: 'Generic' },
    { tld: '.link', price: 11.99, category: 'Generic' },
    { tld: '.email', price: 24.99, category: 'Communication' },
    { tld: '.chat', price: 34.99, category: 'Communication' },
    { tld: '.live', price: 29.99, category: 'Communication' },
    { tld: '.social', price: 34.99, category: 'Social Media' },
    { tld: '.community', price: 34.99, category: 'Social Media' },
    { tld: '.network', price: 24.99, category: 'Technology' },
    { tld: '.systems', price: 24.99, category: 'Technology' },
    { tld: '.computer', price: 34.99, category: 'Technology' },
    { tld: '.software', price: 34.99, category: 'Technology' },
    { tld: '.codes', price: 54.99, category: 'Technology' },
  ];

  const categories = Array.from(new Set(domainExtensions.map(ext => ext.category))).sort();
  const [selectedCategory, setSelectedCategory] = React.useState<string>('All');
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredExtensions = domainExtensions.filter(ext => {
    const matchesCategory = selectedCategory === 'All' || ext.category === selectedCategory;
    const matchesSearch = ext.tld.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">All Domain Extensions</h2>
              <p className="text-blue-100">Choose from {domainExtensions.length}+ available domain extensions</p>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search extensions (e.g., .com, .tech)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
              />
            </div>
            
            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
              >
                <option value="All">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Extensions Grid */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredExtensions.map((ext) => (
              <div
                key={ext.tld}
                className={`bg-gradient-to-br from-white via-gray-50 to-gray-100 border rounded-xl p-4 text-center hover:shadow-lg transition-all duration-200 hover:border-primary-navy group cursor-pointer ${
                  ext.popular ? 'border-orange-300 bg-gradient-to-br from-orange-50 via-yellow-50 to-orange-50' : 'border-gray-200'
                }`}
              >
                {ext.popular && (
                  <div className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-orange-400 to-yellow-500 text-white text-xs font-bold rounded-full mb-2">
                    POPULAR
                  </div>
                )}
                <div className="text-lg font-bold text-gray-800 group-hover:text-primary-navy mb-1">
                  {ext.tld}
                </div>
                <div className="text-sm text-gray-500 mb-2">{ext.category}</div>
                <div className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 font-bold">
                  ${ext.price}/yr
                </div>
              </div>
            ))}
          </div>
          
          {filteredExtensions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No extensions found matching your criteria.</p>
              <p className="text-gray-400 mt-2">Try adjusting your search or category filter.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-sm text-gray-600 mb-4 md:mb-0">
              Showing {filteredExtensions.length} of {domainExtensions.length} extensions
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white rounded-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg"
              >
                Start Domain Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainExtensionsModal;